import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal, SelectInput } from 'vqp-common';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Icon } from '../common';

const ModalContent = styled.div`
  padding: 24px;
  min-width: 420px;
  max-width: 560px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.colors?.textBold || '#333'};
  margin: 0 0 8px 0;
`;

const Hint = styled.p`
  font-size: 13px;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
  margin: 0 0 16px 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  border: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};
  background: ${(p) => (p.$active ? p.theme.colors?.primary || '#173A6D' : 'white')};
  color: ${(p) => (p.$active ? 'white' : p.theme.colors?.textBold || '#333')};
  border-radius: 5px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const CategorySelectWrap = styled.div`
  margin-bottom: 12px;
  max-width: 100%;

  > div {
    width: 100%;
  }
`;

const List = styled.div`
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};
  border-radius: 8px;
  background: ${(p) => p.theme.colors?.lightGrey || '#fafafa'};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: white;
  border-bottom: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};
  box-shadow: ${(p) => (p.$isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none')};

  &:last-child {
    border-bottom: none;
  }
`;

const DragHandle = styled.span`
  display: inline-flex;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const OrderBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
  background: ${(p) => p.theme.colors?.lightGrey || '#eee'};
  min-width: 28px;
  text-align: center;
  padding: 4px 8px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const ItemName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colors?.textBold || '#333'};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};

  & > button {
    min-width: 100px;
    height: 36px;
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
`;

const TAB_CATEGORIES = 'categories';
const TAB_PRODUCTS = 'products';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
};

const cloneCategories = (categories = []) =>
  categories.map((cat, index) => ({
    id: cat.id,
    name: cat.name,
    order: cat.order != null ? cat.order : index + 1,
    products: (cat.products || []).map((product, productIndex) => ({
      id: product.id,
      name: product.name,
      order: product.order != null ? product.order : productIndex + 1,
    })),
  }));

export const OrganizeMenuModal = ({ categories, isOpen, onClose, onSave, loading }) => {
  const [tab, setTab] = useState(TAB_CATEGORIES);
  const [items, setItems] = useState([]);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  useEffect(() => {
    if (isOpen && categories) {
      const cloned = cloneCategories(categories);
      setItems(cloned);
      setTab(TAB_CATEGORIES);
      setSelectedCategoryIndex(0);
    }
  }, [isOpen, categories]);

  const categoryOptions = useMemo(
    () => items.map((cat) => ({ value: cat.id, name: cat.name })),
    [items]
  );

  const selectedCategory = items[selectedCategoryIndex] || null;
  const products = selectedCategory?.products || [];

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    if (tab === TAB_CATEGORIES) {
      setItems(reorder(items, result.source.index, result.destination.index));
      return;
    }

    if (!selectedCategory) return;

    const nextProducts = reorder(products, result.source.index, result.destination.index);
    setItems((prev) =>
      prev.map((cat, index) =>
        index === selectedCategoryIndex ? { ...cat, products: nextProducts } : cat
      )
    );
  };

  const handleSave = () => {
    if (!items.length || loading) return;
    const productsPayload = items.flatMap((cat) =>
      (cat.products || []).map((product) => ({
        id: product.id,
        order: product.order,
      }))
    );
    onSave({
      categories: items.map((cat) => ({ id: cat.id, name: cat.name, order: cat.order })),
      products: productsPayload,
    });
  };

  const renderList = (list, droppableId, prefix) => {
    if (!list.length) {
      return (
        <EmptyState>
          {tab === TAB_CATEGORIES
            ? 'Nenhuma categoria para organizar.'
            : 'Esta categoria não possui produtos.'}
        </EmptyState>
      );
    }

    return (
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <List ref={provided.innerRef} {...provided.droppableProps}>
            {list.map((item, index) => (
              <Draggable
                key={String(item.id)}
                draggableId={`${prefix}-${item.id}`}
                index={index}
                isDragDisabled={loading}
              >
                {(dragProvided, snapshot) => (
                  <Row
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    $isDragging={snapshot.isDragging}
                  >
                    <DragHandle {...dragProvided.dragHandleProps}>
                      <Icon name="drag" width="16px" height="16px" />
                    </DragHandle>
                    <OrderBadge>{item.order}</OrderBadge>
                    <ItemName title={item.name}>{item.name}</ItemName>
                  </Row>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={loading ? undefined : onClose} boxWidth="560px">
      <ModalContent>
        <ModalTitle>Organizar cardápio</ModalTitle>
        <Hint>
          {tab === TAB_CATEGORIES
            ? 'Arraste as categorias para definir a ordem de exibição.'
            : 'Selecione a categoria e arraste os produtos para definir a ordem.'}
        </Hint>

        <Tabs>
          <TabButton
            type="button"
            $active={tab === TAB_CATEGORIES}
            onClick={() => setTab(TAB_CATEGORIES)}
            disabled={loading}
          >
            Categorias
          </TabButton>
          <TabButton
            type="button"
            $active={tab === TAB_PRODUCTS}
            onClick={() => setTab(TAB_PRODUCTS)}
            disabled={loading}
          >
            Produtos
          </TabButton>
        </Tabs>

        {tab === TAB_PRODUCTS && (
          <CategorySelectWrap>
            {categoryOptions.length > 0 ? (
              <SelectInput
                options={categoryOptions}
                selected={selectedCategoryIndex}
                onSelect={setSelectedCategoryIndex}
              />
            ) : (
              <EmptyState>Nenhuma categoria disponível.</EmptyState>
            )}
          </CategorySelectWrap>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          {tab === TAB_CATEGORIES
            ? renderList(items, 'organize-categories', 'category')
            : renderList(products, `organize-products-${selectedCategory?.id || 'none'}`, 'product')}
        </DragDropContext>

        <Actions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            className="primary"
            onClick={handleSave}
            disabled={loading || items.length === 0}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Actions>
      </ModalContent>
    </Modal>
  );
};
